import { useCallback } from 'react';

import { ExpandedRowT } from 'business/definitions';
import { IRowNode, RowNode } from 'ag-grid-community';

/**
 * Hook to get a function with two parameters :
 * - node: a pivot grid RowNode
 * - nodesToExpand : a tree of node which must be expanded
 * The function returns true if the node is contained in nodesToExpand. Else false.
 */
export const useMustRowBeExpanded = (): ((
    node: IRowNode,
    rowsToExpand: ExpandedRowT[],
) => boolean) => {
    const getRowChildren = useCallback(
        (node: RowNode, rowsToExpand: ExpandedRowT[]): ExpandedRowT[] => {
            const nodeChildrenNodes =
                node.level === 0 || node.parent === null
                    ? rowsToExpand
                    : getRowChildren(node.parent, rowsToExpand);

            const row = nodeChildrenNodes.find((r) => r.value === node.key);
            const children = row === undefined ? [] : row.children;
            return children;
        },
        [],
    );

    const mustBeExpanded = useCallback(
        (node: RowNode, rowsToExpand: ExpandedRowT[]): boolean => {
            if (node.level === 0 || node.parent === null) {
                return rowsToExpand.some((row) => row.value === node.key);
            }

            const parentChildrenRows = getRowChildren(
                node.parent,
                rowsToExpand,
            );

            return parentChildrenRows.some((row) => row.value === node.key);
        },
        [],
    );

    return mustBeExpanded;
};
